import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // Safari (particularly iOS) automatically scrolls the page when a <details>
    // element is toggled, and it does so using stale layout info before the
    // content has finished expanding. That causes the accordion header to be
    // scrolled out of view instead of staying put. Take over the toggle and
    // scroll manually once the new state has settled.
    event.preventDefault();

    const willOpen = !this.details.open;
    this.details.open = willOpen;

    if (willOpen) {
      this.#closeSiblingsInGroup();

      if (this.#isOnPdp) {
        this.#scrollSummaryIntoViewWhenSettled();
      } else {
        requestAnimationFrame(() => {
          this.summary.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      }
    }
  };

  /**
   * Accordion rows rendered by the `accordion` block behave as a group: opening
   * one closes whichever sibling is open. Only user-driven opens go through
   * here, so the default open state set on load (see `#setDefaultOpenState`) is
   * left alone. Accordions outside such a group (filters, drawers, size guide)
   * have no group to collapse and are unaffected.
   */
  #closeSiblingsInGroup() {
    const group = this.closest('.accordion');

    if (!group) return;

    for (const sibling of group.querySelectorAll('accordion-custom')) {
      // Skip self, and any accordion nested inside this row's own content.
      if (this.contains(sibling)) continue;

      const details = sibling.querySelector('details');

      if (details instanceof HTMLDetailsElement) details.open = false;
    }
  }

  /**
   * Whether this accordion is on the product page. The sticky-header-aware
   * scroll adjustment below assumes the accordion sits in the main page
   * scroll flow, which isn't true for accordions used inside fixed-position
   * drawers/modals (nav drawer, filters, size guide, etc.) elsewhere on the
   * site — those rely on the default `scrollIntoView` finding their own
   * scrollable ancestor instead.
   */
  get #isOnPdp() {
    const template = document.getElementById('MainContent')?.dataset.template ?? '';
    return template === 'product' || template.startsWith('product.');
  }

  /**
   * The details content animates open (see `.details-content` in base.css),
   * so its final layout isn't in place until that transition finishes.
   * Scrolling too early measures the summary against an in-between state,
   * which can leave it under the header. Wait for the transition to end
   * before scrolling, falling back to a timeout in case it doesn't fire
   * (e.g. reduced-motion, or the property transitioned isn't the one we're
   * listening for).
   */
  #scrollSummaryIntoViewWhenSettled() {
    const content = /** @type {HTMLElement | null} */ (this.details.querySelector('.details-content'));
    let settled = false;

    const scrollOnce = () => {
      if (settled) return;
      settled = true;
      content?.removeEventListener('transitionend', handleTransitionEnd);
      this.#scrollSummaryIntoView();
    };

    /** @param {TransitionEvent} event */
    const handleTransitionEnd = (event) => {
      if (event.target === content && event.propertyName === 'block-size') scrollOnce();
    };

    if (content) content.addEventListener('transitionend', handleTransitionEnd);

    setTimeout(scrollOnce, 250);
  }

  /**
   * Scrolls the summary into view, keeping it below any sticky/fixed header.
   * `Element.scrollIntoView` doesn't know about elements that overlay the
   * viewport (like a sticky header), so it can leave the summary hidden
   * underneath it. This measures the header's current overlap and, if the
   * summary would end up underneath it, scrolls just far enough to clear it.
   */
  #scrollSummaryIntoView() {
    const summaryRect = this.summary.getBoundingClientRect();
    const headerOffset = this.#currentStickyHeaderOffset();

    if (summaryRect.top < headerOffset) {
      const scrollBy = summaryRect.top - headerOffset;
      window.scrollBy({ top: scrollBy, behavior: 'smooth' });
      return;
    }

    if (summaryRect.top >= window.innerHeight) {
      this.summary.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  /**
   * Measures how much of the viewport's top is currently covered by a
   * sticky/fixed header, if any.
   * @returns {number}
   */
  #currentStickyHeaderOffset() {
    const headerSection = document.querySelector('.header-section');

    if (!headerSection) return 0;

    const { bottom } = headerSection.getBoundingClientRect();

    return bottom > 0 ? bottom : 0;
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
